import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { CompanyEnabledGuard } from './modules/companies/guards/company-enabled.guard';
import { CompanyOnlyGuard } from './modules/companies/guards/company-only-guard.guard';
import { permissionGuard } from 'app/core/permissions/permission.guard';
import { personalSpaceGuard } from 'app/core/workspace/personal-space.guard';

export const appRoutes: Route[] = [
    { path: '', pathMatch: 'full', redirectTo: 'clients/list' },
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'clients/list' },
    {
        path: '',
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [{ path: '', loadChildren: () => import('app/modules/auth/auth.module').then(m => m.AuthModule) }],
    },
    {
        path: '',
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [{ path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') }],
    },
    {
        path: 'meeting/:sessionId',
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [{ path: '', loadComponent: () => import('app/modules/interviews/meeting.component').then(m => m.MeetingComponent) }],
    },

    // Routes protégées
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'companies',
                loadChildren: () => import('app/modules/companies/companies.module').then(m => m.CompaniesModule),
            },
            {
                path: 'sprint/:id',
                loadChildren: () => import('app/modules/ticket-management/ticket-management.module').then(m => m.TicketManagementModule),
            },
            {
                path: 'projets',
                loadChildren: () => import('app/modules/ticket-management/projects.module').then(m => m.ProjectsModule),
            },
            {
                path: 'clients',
                canActivate: [permissionGuard('CLIENT:READ')],
                loadChildren: () => import('app/modules/client/client.module').then(m => m.ClientModule),
            },
            {
                path: 'administration',
                canActivate: [permissionGuard('ADMIN:MANAGE')],
                loadChildren: () => import('app/modules/administration/administration.module').then(m => m.AdministrationModule),
            },
            {
                path: 'configuration',
                loadChildren: () => import('app/modules/settings/configuration/configuration.routes').then(m => m.configurationRoutes),
            },
            {
                path: 'flags',
                loadChildren: () => import('app/modules/flags/flag.module').then(m => m.FlagModule)
            },
            {
                path: 'products',
                loadChildren: () => import('app/modules/products/products.module').then(m => m.ProductsModule),
            },
            {
                path: 'crm-actions',
                loadChildren: () => import('app/modules/crm-actions/crm-actions.module').then(m => m.CrmActionsModule),
            },
        ],
    },

    // Routes protégées
    {
        path: '',
        canActivate: [AuthGuard, CompanyEnabledGuard],
        canActivateChild: [AuthGuard, CompanyEnabledGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'contacts',
                canActivate: [permissionGuard('CONTACT:READ')],
                loadChildren: () => import('app/modules/contact/contacts.module').then(m => m.ContactsModule),
            },
            {
                path: 'resources',
                canActivate: [permissionGuard('RESOURCE:READ')],
                loadChildren: () => import('app/modules/resources/resources.module').then(m => m.ResourcesModule),
            },
            {
                path: 'candidates',
                canActivate: [permissionGuard('CANDIDATE:READ')],
                loadChildren: () => import('app/modules/candidates/candidates.module').then(m => m.CandidatesModule),
            },
            {
                path: 'leaves',
                canActivate: [permissionGuard('ACTIVITY:READ')],
                loadChildren: () =>
                    import('app/modules/leave-management/leave-management.module')
                        .then(m => m.LeaveManagementModule),
            },
            {
                path: 'subcontractors',
                canActivate: [permissionGuard('RESOURCE:READ')],
                loadChildren: () => import('app/modules/subcontractors/subcontractors.module').then(m => m.SubcontractorsModule),
            },
            {
                path: 'requirements',
                canActivate: [permissionGuard('REQUIREMENT:READ')],
                loadChildren: () => import('app/modules/requirements/requirements.module').then(m => m.RequirementsModule),
            },
            {
                path: 'cra',
                canActivate: [permissionGuard('ACTIVITY:READ')],
                loadChildren: () => import('app/modules/cra/cra.module').then(m => m.CraModule),
            },
            {
                // Espace personnel d'un collaborateur qui est aussi une ressource : ses CRA, congés, notes de
                // frais et documents, quel que soit son rôle (le backend le limite à sa propre fiche).
                path: 'mon-espace',
                canActivate: [personalSpaceGuard],
                data: { personalSpace: true },
                children: [
                    { path: 'cra', loadChildren: () => import('app/modules/cra/cra.module').then(m => m.CraModule) },
                    { path: 'historique-cra', loadChildren: () => import('app/modules/cra/historique-cra.routes') },
                    { path: 'note-de-frais', loadChildren: () => import('app/modules/cra/note-de-frais.routes') },
                    { path: 'ca-suivi', loadChildren: () => import('app/modules/cra/ca-suivi.routes') },
                    {
                        path: 'conges',
                        loadChildren: () =>
                            import('app/modules/leave-management/leave-management.module').then(m => m.LeaveManagementModule),
                    },
                    { path: 'fiche', loadChildren: () => import('app/modules/resources/resources.module').then(m => m.ResourcesModule) },
                ],
            },
            {
                // Tableau de bord KPI : ce que chaque membre de la société a produit sur une période
                path: 'dashboard',
                canActivate: [CompanyOnlyGuard, permissionGuard('DASHBOARD:READ')],
                loadComponent: () =>
                    import('app/modules/dashboard/kpi-dashboard.component').then(m => m.KpiDashboardComponent),
            },
            {
                path: 'entretiens',
                canActivate: [CompanyOnlyGuard, permissionGuard('INTERVIEW:READ')],
                loadChildren: () => import('app/modules/interviews/interview.routes'),
            },

            {
                // Liste des factures émises (filtres, statut, PDF, envoi) : /factures/list
                path: 'factures',
                canActivate: [CompanyOnlyGuard, permissionGuard('BILLING:READ')],
                loadChildren: () => import('app/modules/facture/facture-routing.module').then(m => m.FactureRoutingModule),
            },

            // -----------------------------------------------------------------------------
            // NON ROUTÉ VOLONTAIREMENT — fonctionnalités incomplètes
            //
            // 'missions' : aucun module correspondant n'existe dans app/modules. Les entrées
            //   de menu associées ont été désactivées dans mock-api/common/navigation/data.ts.
            // -----------------------------------------------------------------------------
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [

            // -----------------------------------------------------------------------------
            // SUPPRIMÉ : écrans de démonstration Fuse (/dashboards/*, /pages/*, /ui/*, /docs/*)
            // alimentés par le mock-api et sans lien avec le métier. Seule la page 404 de la
            // démo est conservée (route '404-not-found' ci-dessous).
            // -----------------------------------------------------------------------------

            // Dashboards de démo Fuse (mock-api), rouverts comme référence visuelle.
            {
                path: 'dashboards', children: [
                    { path: 'project', loadChildren: () => import('app/modules/admin/dashboards/project/project.routes') },
                    { path: 'analytics', loadChildren: () => import('app/modules/admin/dashboards/analytics/analytics.routes') },
                    { path: 'finance', loadChildren: () => import('app/modules/admin/dashboards/finance/finance.routes') },
                    { path: 'crypto', loadChildren: () => import('app/modules/admin/dashboards/crypto/crypto.routes') },
                ],
            },

            // Apps
            {
                path: 'apps', children: [
                    // -------------------------------------------------------------------------
                    // DÉSACTIVÉ : modules de démo Fuse alimentés par le mock-api, sans back réel.
                    // Réactiver la route une fois la fonctionnalité implémentée côté serveur.
                    // NB : le code des composants/services reste en place. ContactsService est
                    // toujours importé par les écrans métier (liste des pays) : ne pas supprimer.
                    // Le vrai chat est le « quick-chat » du layout, branché sur chat-service.
                    // -------------------------------------------------------------------------
                    // { path: 'academy', loadChildren: () => import('app/modules/admin/apps/academy/academy.routes') },
                    // { path: 'chat', loadChildren: () => import('app/modules/admin/apps/chat/chat.routes') },
                    // { path: 'contacts', loadChildren: () => import('app/modules/admin/apps/contacts/contacts.routes') },
                    {
                        path: 'historique-cra',
                        canActivate: [permissionGuard('ACTIVITY:READ')],
                        loadChildren: () => import('app/modules/cra/historique-cra.routes')
                    },
                    {
                        path: 'validation-cra',
                        canActivate: [permissionGuard('ACTIVITY:READ')],
                        loadChildren: () => import('app/modules/cra/validation-cra.routes')
                    },
                    {
                        path: 'note-de-frais',
                        canActivate: [permissionGuard('BILLING:READ')],
                        loadChildren: () => import('app/modules/cra/note-de-frais.routes')
                    },
                    
                    {
                        path: 'ca-suivi',
                        canActivate: [permissionGuard('BILLING:READ')],
                        loadChildren: () => import('app/modules/cra/ca-suivi.routes')
                    },
                    {
                        path: 'resource-cras/:id',
                        canActivate: [permissionGuard('ACTIVITY:READ')],
                        loadChildren: () => import('app/modules/cra/resource-cras.routes')
                    },
                    {
                        path: 'subcontractor-cras/:id',
                        canActivate: [permissionGuard('ACTIVITY:READ')],
                        loadChildren: () => import('app/modules/cra/resource-cras.routes')
                    },

                    // DÉSACTIVÉ : démo Fuse (mock-api), voir le bloc de commentaire ci-dessus.
                    // {
                    //     path: 'ecommerce',
                    //     loadChildren: () => import('app/modules/admin/apps/ecommerce/ecommerce.routes'),
                    // },

                    // Seul module « apps » de la démo réellement branché sur le back
                    // (crmrh FileManagerController + service d'indexation) : on le conserve.
                    {
                        path: 'file-manager',
                        canActivate: [permissionGuard('DOCUMENT:READ')],
                        loadChildren: () => import('app/modules/admin/apps/file-manager/file-manager.routes'),
                    },

                    // DÉSACTIVÉ : démo Fuse (mock-api), voir le bloc de commentaire ci-dessus.
                    // {
                    //     path: 'help-center',
                    //     loadChildren: () => import('app/modules/admin/apps/help-center/help-center.routes'),
                    // },
                    // { path: 'mailbox', loadChildren: () => import('app/modules/admin/apps/mailbox/mailbox.routes') },
                    // { path: 'notes', loadChildren: () => import('app/modules/admin/apps/notes/notes.routes') },
                    // {
                    //     path: 'scrumboard',
                    //     loadChildren: () => import('app/modules/admin/apps/scrumboard/scrumboard.routes'),
                    // },
                    // { path: 'tasks', loadChildren: () => import('app/modules/admin/apps/tasks/tasks.routes') },
                ],
            },

            // 404 & Catch all
            {
                path: '404-not-found',
                pathMatch: 'full',
                loadChildren: () => import('app/modules/admin/pages/error/error-404/error-404.routes'),
            },
            { path: '**', redirectTo: '404-not-found' },
        ],
    },
];
